import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { IntlShape, useIntl } from 'react-intl';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Panel } from '../../components/layout/Panel';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { ReferencedWorkDTO, WorkOrganisationDTO, WorkOrganisationOtherDTO } from '../../../../api/core';
import { getDefaultWorkOrganisation, getDefaultWorkOrganisationOther } from '../../../../Settings/emptyModels';
import React, { ReactElement, ReactNode } from 'react';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { LabelledOrganisationSuggester } from '../../../components/form/LabelledOrganisationSuggester';
import {
  referencedOrganisationToSelectedOrganisation,
  selectedOrganisationToReferencedOrganisation,
} from '../../components/suggester/OrganisationSuggester';

export const OrganisationsPage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  return <Block title={`work.header.organisations`}>
    <PermissionGate always={false} entity={'work_generic'} field="work_organisations">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'organisations'} className={'col-12'}
               title={'work.attr.workOrganisations'}>
          <MultiInputControls<WorkOrganisationDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workOrganisations}
            addNew={(rows: WorkOrganisationDTO[]): number => rows.push(getDefaultWorkOrganisation())}
            onChange={(organisations: WorkOrganisationDTO[]): void => props.setField('workOrganisations', organisations)}
            renderLines={(parent: WorkOrganisationDTO, onLineChange: (newValues: WorkOrganisationDTO) => void, actions: Actions, idx: number): ReactNode => {
              return <InputGroup
                delete={{
                  action: actions.delete,
                  disabled: !checkPermission().canWrite,
                }}
              >
                <LabelledOrganisationSuggester
                  id={`organisations-${idx}-organisation`}
                  autoFocus={props.autoFocus}
                  value={(parent.organisation ? [parent.organisation] : []).map(referencedOrganisationToSelectedOrganisation)}
                  validation={props.validation?.root?.fields?.work_organisations.children[idx].fields.organisation}
                  permissions={checkPermission('organisation')}
                  onChange={(organisations: ReferencedWorkDTO[]): void => onLineChange({
                    ...parent,
                    organisation: organisations.length > 0 ? organisations.map(selectedOrganisationToReferencedOrganisation).pop() : undefined,
                  })}
                  label={'work.attr.workOrganisation.attr.organisation'}
                  multiple={false}
                />
                <LabelledDropdown
                  id={`organisations-${idx}-role`}
                  value={parent.role}
                  permissions={checkPermission('role')}
                  validation={props.validation?.root?.fields?.work_organisations.children[idx].fields.role}
                  onChange={(role: string) => onLineChange({...parent, role})}
                  label={'work.attr.workOrganisation.attr.role'}
                  options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                />
              </InputGroup>;
            }}
          />
        </Panel>
      }
    </PermissionGate>

    <PermissionGate always={false} entity={'work_generic'} field="work_organisation_others">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'organisationOthers'} className={'col-12'}
               title={'work.attr.workOrganisationOthers'}>
          <MultiInputControls<WorkOrganisationOtherDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workOrganisationOthers}
            addNew={(rows: WorkOrganisationOtherDTO[]): number => rows.push(getDefaultWorkOrganisationOther())}
            onChange={(organisations: WorkOrganisationOtherDTO[]): void => props.setField('workOrganisationOthers', organisations)}
            renderLines={(parent: WorkOrganisationOtherDTO, onLineChange: (newValues: WorkOrganisationOtherDTO) => void, actions: Actions, idx: number): ReactNode => {
              return <InputGroup
                delete={{
                  action: actions.delete,
                  disabled: !checkPermission().canWrite,
                }}
              >
                <LabelledInputText
                  id={`organisationOthers-${idx}-name`}
                  autoFocus={props.autoFocus}
                  value={parent.name || ''}
                  validation={props.validation?.root?.fields?.work_organisation_others.children[idx].fields.name}
                  permissions={checkPermission('name')}
                  onChange={(name: string): void => onLineChange({...parent, name})}
                  label={'work.attr.workOrganisation.attr.otherName'}
                />
                <LabelledDropdown
                  id={`organisationOthers-${idx}-role`}
                  value={parent.role}
                  permissions={checkPermission('role')}
                  validation={props.validation?.root?.fields?.work_organisation_others.children[idx].fields.role}
                  onChange={(role: string) => onLineChange({...parent, role})}
                  label={'work.attr.workOrganisation.attr.role'}
                  options={getVocabularyOptions(intl.locale, 'corporation_role_list')}
                />
              </InputGroup>;
            }}
          />
        </Panel>
      }
    </PermissionGate>
  </Block>;
};
